import React, { useEffect, useRef, useState } from "react";
import DashboardWrapper from "./components/DashboardWrapper";
import { useTranslation } from "react-i18next";
import {
  useGetCustomerReferralsQuery,
  useSendReferralInviteMutation,
} from "../../features/customerApiSlice";
import ReferralInviteModal from "./components/ReferralInviteModal";

function ReferralCode() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useGetCustomerReferralsQuery();
  const [sendReferralInvite, { isLoading: isSendingInvite }] = useSendReferralInviteMutation();
  const [copiedField, setCopiedField] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteError, setInviteError] = useState("");
  const copiedTimerRef = useRef(null);

  const currentData = data;
  const referral = currentData?.referral || {};
  const stats = currentData?.stats || {};
  const rewards = currentData?.rewards || [];
  const isEligible = Boolean(referral?.isEligible && referral?.code);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  const formatCredit = (amount) => `€${Number(amount || 0).toFixed(0)}`;

  const formatMoney = (amount, currency = "EUR") => {
    const normalizedAmount = Number(amount);
    if (!Number.isFinite(normalizedAmount)) return "";

    return new Intl.NumberFormat(i18n.language || "en", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(normalizedAmount);
  };

  const formatDate = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return "";

    return new Intl.DateTimeFormat(i18n.language || "en", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(parsedDate);
  };

  const getRewardStatusLabel = (reward) => {
    const nextPaymentDate = formatDate(reward.nextPaymentDate);
    const appliedDate = formatDate(reward.appliedAt);
    const appliedPaymentAmount = formatMoney(
      reward.appliedPaymentAmount,
      reward.appliedPaymentCurrency || reward.currency,
    );

    if (["pending", "applied_to_subscription"].includes(reward.status) && nextPaymentDate) {
      return t("ReferralPage_Status_nextPaymentDate", { date: nextPaymentDate });
    }

    if (reward.status === "applied" && appliedDate && appliedPaymentAmount) {
      return t("ReferralPage_Status_appliedPaymentDate", {
        amount: appliedPaymentAmount,
        date: appliedDate,
      });
    }

    if (reward.status === "applied" && appliedDate) {
      return t("ReferralPage_Status_appliedPaymentDateNoAmount", { date: appliedDate });
    }

    return t(`ReferralPage_Status_${reward.status}`, reward.status);
  };

  const handleCopy = (field, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopiedField(field);

    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCloseInviteModal = () => {
    if (isSendingInvite) return;
    setIsInviteModalOpen(false);
    setInviteEmail("");
    setInviteMessage("");
    setInviteError("");
  };

  const handleSendInvite = async (event) => {
    event.preventDefault();
    setInviteMessage("");
    setInviteError("");

    const email = inviteEmail.trim();
    if (!email) {
      setInviteError(t("ReferralPage_EmailRequired"));
      return;
    }

    try {
      await sendReferralInvite({ email }).unwrap();
      setInviteEmail("");
      setInviteMessage(t("ReferralPage_EmailSent"));
    } catch (error) {
      setInviteError(error?.data?.message || t("ReferralPage_EmailFailed"));
    }
  };

  return (
    <DashboardWrapper title={t("ReferralCode_Title")}>
      <div className="t-w-full lg:t-max-w-4xl">
        <div className="t-flex t-flex-col t-gap-7">
          <div>
            <h1 className="t-m-0 t-font-sans t-text-3xl t-font-bold t-text-tertiary">
              {t("ReferralPage_Heading")}
            </h1>
            <p className="t-mt-2 t-max-w-2xl t-font-sans t-text-base t-font-semibold t-text-gray-500">
              {t("ReferralPage_Subheading")}
            </p>
          </div>

          <div className="t-grid t-grid-cols-1 t-gap-4 md:t-grid-cols-2">
            <div className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-6">
              <p className="t-m-0 t-font-sans t-text-base t-font-semibold t-text-gray-500">
                {t("ReferralPage_ActiveFriends")}
              </p>
              <strong className="t-mt-4 t-block t-font-sans t-text-4xl t-text-tertiary">
                {stats?.activeFriendsCount || 0}
              </strong>
              <p className="t-m-0 t-font-sans t-text-sm t-font-semibold t-text-gray-400">
                {t("ReferralPage_ActiveFriendsLabel")}
              </p>
            </div>
            <div className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-6">
              <p className="t-m-0 t-font-sans t-text-base t-font-semibold t-text-gray-500">
                {t("ReferralPage_EarnedCredit")}
              </p>
              <strong className="t-mt-4 t-block t-font-sans t-text-4xl t-text-[#827600]">
                {formatCredit(stats?.earnedCredit)}
              </strong>
              <p className="t-m-0 t-font-sans t-text-sm t-font-semibold t-text-gray-400">
                {t("ReferralPage_EarnedCreditLabel")}
              </p>
            </div>
          </div>

          <div className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-6 md:t-p-8">
            {isLoading ? (
              <p className="t-m-0 t-font-sans t-text-base t-text-gray-500">{t("Loading")}</p>
            ) : isEligible ? (
              <>
                <h2 className="t-m-0 t-font-sans t-text-2xl t-font-bold t-text-tertiary">
                  {t("ReferralPage_LinkHeading")}
                </h2>
                <p className="t-mt-2 t-font-sans t-text-base t-font-semibold t-text-gray-500">
                  {t("ReferralPage_LinkSubheading")}
                </p>

                <div className="t-mt-8 t-flex t-flex-col t-gap-5">
                  <div>
                    <p className="t-mb-2 t-font-sans t-text-sm t-font-bold t-text-gray-600">
                      {t("ReferralPage_ShareLink")}
                    </p>
                    <div className="t-flex t-flex-col t-gap-3 md:t-flex-row">
                      <div className="t-flex-1 t-rounded-lg t-border t-border-gray-200 t-bg-gray-50 t-px-5 t-py-3 t-font-mono t-text-sm t-font-bold t-text-gray-700">
                        {referral.link}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopy("link", referral.link)}
                        className={`t-min-w-32 t-rounded-lg t-border t-bg-white t-px-6 t-py-3 t-font-sans t-text-sm t-font-bold hover:t-bg-gray-50 disabled:t-cursor-not-allowed disabled:t-opacity-60 ${
                          copiedField === "link"
                            ? "t-border-green-200 t-text-green-600"
                            : "t-border-gray-200 t-text-tertiary"
                        }`}
                      >
                        {copiedField === "link"
                          ? t("ReferralPage_Copied")
                          : t("ReferralPage_CopyLink")}
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="t-mb-2 t-font-sans t-text-sm t-font-bold t-text-gray-600">
                      {t("ReferralPage_ShareCode")}
                    </p>
                    <div className="t-flex t-flex-col t-gap-3 md:t-flex-row">
                      <div className="t-flex-1 t-rounded-lg t-border t-border-gray-200 t-bg-gray-50 t-px-5 t-py-3 t-font-mono t-text-sm t-font-bold t-text-gray-700">
                        {referral.code}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopy("code", referral.code)}
                        className={`t-min-w-32 t-rounded-lg t-border t-bg-white t-px-6 t-py-3 t-font-sans t-text-sm t-font-bold hover:t-bg-gray-50 disabled:t-cursor-not-allowed disabled:t-opacity-60 ${
                          copiedField === "code"
                            ? "t-border-green-200 t-text-green-600"
                            : "t-border-gray-200 t-text-tertiary"
                        }`}
                      >
                        {copiedField === "code"
                          ? t("ReferralPage_Copied")
                          : t("ReferralPage_CopyCode")}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="t-mt-5">
                  <button
                    type="button"
                    onClick={() => setIsInviteModalOpen(true)}
                    className="t-rounded-lg t-bg-tertiary t-px-6 t-py-3 t-font-sans t-text-sm t-font-bold t-text-white hover:t-opacity-90"
                  >
                    {t("ReferralPage_SendEmail")}
                  </button>
                </div>

                <div className="t-mt-8 t-grid t-gap-6 t-border-t t-border-gray-200 t-pt-8 md:t-grid-cols-3">
                  {[1, 2, 3].map((step) => (
                    <div key={step}>
                      <span className="t-flex t-h-8 t-w-8 t-items-center t-justify-center t-rounded-full t-bg-[#edf8d8] t-font-sans t-text-sm t-font-bold t-text-tertiary">
                        {step}
                      </span>
                      <h3 className="t-mt-3 t-font-sans t-text-base t-font-bold t-text-tertiary">
                        {t(`ReferralPage_Step${step}Title`)}
                      </h3>
                      <p className="t-m-0 t-font-sans t-text-sm t-font-semibold t-text-gray-500">
                        {t(`ReferralPage_Step${step}Body`)}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div>
                <h2 className="t-m-0 t-font-sans t-text-2xl t-font-bold t-text-tertiary">
                  {t("ReferralPage_InactiveHeading")}
                </h2>
                <p className="t-mt-2 t-font-sans t-text-base t-font-semibold t-text-gray-500">
                  {t("ReferralPage_InactiveBody")}
                </p>
              </div>
            )}
          </div>

          {rewards.length > 0 && (
            <div className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-6">
              <h2 className="t-m-0 t-font-sans t-text-xl t-font-bold t-text-tertiary">
                {t("ReferralPage_Activity")}
              </h2>
              <div className="t-mt-4 t-flex t-flex-col t-divide-y t-divide-gray-100">
                {rewards.map((reward) => (
                  <div key={reward.id} className="t-flex t-items-center t-justify-between t-py-3">
                    <span className="t-font-sans t-text-sm t-font-semibold t-text-gray-600">
                      {getRewardStatusLabel(reward)}
                    </span>
                    <strong className="t-font-sans t-text-sm t-text-tertiary">
                      {formatCredit(reward.amount)}
                    </strong>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
      </div>

      {isInviteModalOpen && (
        <ReferralInviteModal
          email={inviteEmail}
          error={inviteError}
          isSending={isSendingInvite}
          message={inviteMessage}
          onChangeEmail={setInviteEmail}
          onClose={handleCloseInviteModal}
          onSubmit={handleSendInvite}
        />
      )}
    </DashboardWrapper>
  );
}

export default ReferralCode;
